import { useEffect, useRef, useState } from 'react';

/** How often a board checks for something the server is still writing. */
export const POLL_INTERVAL_MS = 3000;

/**
 * How long it keeps checking before giving up.
 *
 * Everything polled for (a capture's name, a session's name, a project's
 * panel) arrives in seconds when it arrives at all. When the job writing it
 * dies — an out-of-memory restart took all three down at once — it never
 * arrives, and three unbounded 3s polls from every open tab were a steady
 * stream of requests at the instance that was trying to recover.
 */
export const POLL_WINDOW_MS = 2 * 60 * 1000;

/**
 * A `refetchInterval` that polls while `active`, for at most `windowMs`.
 *
 * The window starts when `active` turns true and resets if it turns false and
 * back, so a new capture waiting on its name gets a fresh two minutes. Returns
 * whether it gave up too, so a caller can stop saying "building…".
 */
export function usePollWindow(
  active: boolean,
  { intervalMs = POLL_INTERVAL_MS, windowMs = POLL_WINDOW_MS } = {},
): { expired: boolean; refetchInterval: false | number } {
  const [expired, setExpired] = useState(false);
  const startedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      startedRef.current = null;
      setExpired(false);
      return;
    }
    startedRef.current ??= Date.now();
    const remaining = startedRef.current + windowMs - Date.now();
    if (remaining <= 0) {
      setExpired(true);
      return;
    }
    const timer = setTimeout(() => {
      setExpired(true);
    }, remaining);
    return () => {
      clearTimeout(timer);
    };
  }, [active, windowMs]);

  return {
    expired: active && expired,
    refetchInterval: active && !expired ? intervalMs : false,
  };
}
